import { ChangeEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import SkeletonImage from '@com/utils/SkeletonImage';
import Spinner from '@com/utils/Spinner';

function AssetsUpdate(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, setUser } = user.useContainer();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileImage, setFileImage] = useState<string>();
  const { updateAvatar } = useAPI();

  const infoOnSubmit = useDebouncedCallback(
    async () => {
      setIsLoading(true);
      const response = await updateAvatar(selectedFile);
      if (userData) {
        setUser({
          ...userData,
          user: { ...userData?.user, avatar: response.avatar },
        });
        toast.success(t('toastMessage.profileUpdated'));
        navigate('/profile/my');
        setIsLoading(false);
      }
    },
    500,
    {
      leading: true,
      trailing: false,
    }
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (files) {
      if (files[0].size > 2048000) {
        toast.error(t('toastMessage.imageFileSizeMax2MB'));
        return;
      }
      setSelectedFile(files[0]);
      setFileImage(URL.createObjectURL(files[0]));
    }
  };

  useEffect(() => {
    if (userData) {
      setFileImage(userData.user?.avatar);
    }
  }, [userData]);

  return (
    <div>
      <div className="sc-card-profile text-center mb-3">
        <SkeletonImage
          className="mx-auto"
          height={256}
          ratio={1}
          src={fileImage}
          width={256}
        />
        <div id="upload-profile">
          <input
            required
            accept={'image/*'}
            aria-label="assets"
            data-test="edit-ava"
            id="tf-upload-img"
            placeholder={t('editProfilePage.facebookPlaceholder')}
            type="file"
            onChange={handleChange}
          />
        </div>
        {selectedFile && (
          <button
            className="btn-upload style2"
            data-test="new-ava-upload-btn"
            type="button"
            onClick={() => infoOnSubmit()}
          >
            {t('editProfilePage.uploadNewPhoto')}
            <Spinner className="ml-2" width={isLoading ? 24 : 0} />
          </button>
        )}
        {!userData?.user?.avatar.startsWith('https://avatars.dicebear.com') &&
          !selectedFile && (
            <button
              className="btn-upload style2"
              data-test="new-ava-upload-btn"
              type="button"
              onClick={() => infoOnSubmit()}
            >
              {t('editProfilePage.delete')}
              <Spinner className="ml-2" width={isLoading ? 24 : 0} />
            </button>
          )}
      </div>
    </div>
  );
}

export default AssetsUpdate;
